import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';

export type CognitoIdentity = {
  providerName?: string;
  providerType?: string;
  issuer?: string;
  primary?: string | boolean;
  dateCreated?: string;
};

/**
 * Parses Cognito `identities` (JSON string on user attributes) for federated Google/Apple users.
 */
export function parseCognitoIdentities(raw: string | undefined): CognitoIdentity[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as CognitoIdentity[]) : [];
  } catch {
    return [];
  }
}

export function isFederatedIdentityList(identities: CognitoIdentity[]): boolean {
  return identities.some((id) => {
    const name = (id.providerName ?? id.providerType ?? '').toLowerCase();
    return name === 'google' || name === 'signinwithapple' || name === 'apple';
  });
}

/** Longer prefixes first so `SignInWithApple_` is not treated as a shorter name. */
const FEDERATED_USERNAME_PREFIXES = ['SignInWithApple_', 'LoginWithAmazon_', 'Facebook_', 'Google_'];

/** True when this Cognito user was created by a social IdP and has no native password. */
export function isFederatedOnlyUsername(username: string | undefined): boolean {
  if (!username) return false;
  return FEDERATED_USERNAME_PREFIXES.some((prefix) => username.startsWith(prefix));
}

/** True when the signed-in Cognito user authenticated via Google or Apple (possibly linked). */
export async function userHasFederatedIdentity(): Promise<boolean> {
  try {
    const attrs = await fetchUserAttributes();
    return isFederatedIdentityList(parseCognitoIdentities(attrs.identities));
  } catch {
    return false;
  }
}

/**
 * Social-only accounts have a provider username (`Google_…`). A password user who later
 * linked Google or Apple keeps their email username and still has a WeTree password.
 */
export async function userIsSocialOnlyAccount(): Promise<boolean> {
  try {
    const { username } = await getCurrentUser();
    if (isFederatedOnlyUsername(username)) return true;
    if (username) return false;
    return userHasFederatedIdentity();
  } catch {
    return false;
  }
}
